import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { EditMealHeader } from "@/components/seller/meal-form/EditMealHeader";
import { EditMealForm } from "@/components/seller/meal-form/EditMealForm";

type PreparationType = "scheduled" | "on_demand";

interface MealData {
  id: string;
  name: string;
  price: number;
  description: string;
  images: string[];
  delivery_available: boolean;
  delivery_fee: number | null;
  cuisine_type: string;
  meal_type: string;
  dietary_tags: string[];
  ingredients: string[];
  allergens: string[];
  calories: number | null;
  carbs: number | null;
  protein: number | null;
  fat: number | null;
  availability_slots: any[];
  has_limited_portions: boolean;
  available_portions: number | null;
  preparation_type: PreparationType;
  prep_time_hours: number | null;
  cutoff_time: string | null;
  max_daily_orders: number | null;
  pickup_window_start: string | null;
  pickup_window_end: string | null;
  available_days: string[];
  pickup_instructions: string | null;
  portion_size_value: number | null;
  portion_size_unit: string | null;
  use_custom_pickup_address: boolean;
  pickup_address: string | null;
  pickup_city: string | null;
  pickup_state: string | null;
  pickup_postal_code: string | null;
}

export default function EditMealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    images: [] as string[],
    deliveryAvailable: false,
    deliveryFee: "",
    cuisine_type: "",
    meal_type: "snack",
    dietary_tags: [] as string[],
    ingredients: [] as string[],
    allergens: [] as string[],
    calories: "",
    carbs: "",
    protein: "",
    fat: "",
    availabilitySlots: [] as any[],
    hasLimitedPortions: false,
    available_portions: "",
    preparation_type: "scheduled" as PreparationType,
    prep_time_hours: "",
    cutoff_time: "",
    max_daily_orders: "",
    pickup_window_start: "",
    pickup_window_end: "",
    available_days: [] as string[],
    pickup_instructions: "",
    portion_size_value: "",
    portion_size_unit: "servings",
    use_custom_pickup_address: false,
    pickup_address: "",
    pickup_city: "",
    pickup_state: "",
    pickup_postal_code: ""
  });

  const { data: meal, isLoading } = useQuery({
    queryKey: ['edit-meal', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meals')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      console.log("Fetched meal data:", data);
      return data as MealData;
    },
  });

  useEffect(() => {
    if (meal) {
      console.log("Setting form data with meal:", meal);
      setFormData({
        name: meal.name || "",
        price: meal.price?.toString() || "",
        description: meal.description || "",
        images: meal.images || [],
        deliveryAvailable: Boolean(meal.delivery_available),
        deliveryFee: meal.delivery_fee?.toString() || "",
        cuisine_type: meal.cuisine_type || "",
        meal_type: meal.meal_type || "snack",
        dietary_tags: meal.dietary_tags || [],
        ingredients: meal.ingredients || [],
        allergens: meal.allergens || [],
        calories: meal.calories?.toString() || "",
        carbs: meal.carbs?.toString() || "",
        protein: meal.protein?.toString() || "",
        fat: meal.fat?.toString() || "",
        availabilitySlots: meal.availability_slots || [],
        hasLimitedPortions: Boolean(meal.has_limited_portions),
        available_portions: meal.available_portions?.toString() || "",
        preparation_type: meal.preparation_type || "scheduled",
        prep_time_hours: meal.prep_time_hours?.toString() || "",
        cutoff_time: meal.cutoff_time || "",
        max_daily_orders: meal.max_daily_orders?.toString() || "",
        pickup_window_start: meal.pickup_window_start || "",
        pickup_window_end: meal.pickup_window_end || "",
        available_days: meal.available_days || [],
        pickup_instructions: meal.pickup_instructions || "",
        portion_size_value: meal.portion_size_value?.toString() || "",
        portion_size_unit: meal.portion_size_unit || "servings",
        use_custom_pickup_address: Boolean(meal.use_custom_pickup_address),
        pickup_address: meal.pickup_address || "",
        pickup_city: meal.pickup_city || "",
        pickup_state: meal.pickup_state || "",
        pickup_postal_code: meal.pickup_postal_code || ""
      });
    }
  }, [meal]);

  const handleFieldChange = (field: string, value: any) => {
    console.log(`Updating ${field}:`, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      console.log("Submitting form data:", formData);

      if (!formData.meal_type) {
        toast({
          title: "Error",
          description: "Please select a dish type",
          variant: "destructive",
        });
        return;
      }

      // Validate required fields for custom address if toggled on
      if (formData.use_custom_pickup_address) {
        if (!formData.pickup_address || !formData.pickup_city || !formData.pickup_state || !formData.pickup_postal_code) {
          toast({
            title: "Error",
            description: "Please fill in all custom address fields",
            variant: "destructive",
          });
          return;
        }
      }

      const updateData = {
        name: formData.name,
        price: parseFloat(formData.price),
        description: formData.description,
        images: formData.images,
        image: formData.images[0] || null,
        delivery_available: formData.deliveryAvailable,
        delivery_fee: formData.deliveryAvailable && formData.deliveryFee ? parseFloat(formData.deliveryFee) : null,
        cuisine_type: formData.cuisine_type,
        meal_type: formData.meal_type,
        dietary_tags: formData.dietary_tags,
        ingredients: formData.ingredients,
        allergens: formData.allergens,
        calories: formData.calories ? parseInt(formData.calories) : null,
        carbs: formData.carbs ? parseFloat(formData.carbs) : null,
        protein: formData.protein ? parseFloat(formData.protein) : null,
        fat: formData.fat ? parseFloat(formData.fat) : null,
        availability_slots: formData.preparation_type === "scheduled" ? formData.availabilitySlots : [],
        has_limited_portions: formData.hasLimitedPortions,
        available_portions: formData.hasLimitedPortions && formData.available_portions ? parseInt(formData.available_portions) : null,
        preparation_type: formData.preparation_type,
        prep_time_hours: formData.preparation_type === "on_demand" && formData.prep_time_hours ? parseInt(formData.prep_time_hours) : null,
        cutoff_time: formData.preparation_type === "on_demand" ? formData.cutoff_time : null,
        max_daily_orders: formData.preparation_type === "on_demand" && formData.max_daily_orders ? parseInt(formData.max_daily_orders) : null,
        pickup_window_start: formData.preparation_type === "on_demand" ? formData.pickup_window_start : null,
        pickup_window_end: formData.preparation_type === "on_demand" ? formData.pickup_window_end : null,
        available_days: formData.preparation_type === "on_demand" ? formData.available_days : [],
        pickup_instructions: formData.pickup_instructions || null,
        portion_size_value: formData.portion_size_value ? parseFloat(formData.portion_size_value) : null,
        portion_size_unit: formData.portion_size_unit || null,
        use_custom_pickup_address: formData.use_custom_pickup_address,
        pickup_address: formData.use_custom_pickup_address ? formData.pickup_address : null,
        pickup_city: formData.use_custom_pickup_address ? formData.pickup_city : null,
        pickup_state: formData.use_custom_pickup_address ? formData.pickup_state : null,
        pickup_postal_code: formData.use_custom_pickup_address ? formData.pickup_postal_code : null
      };

      console.log("Sending update to Supabase:", updateData);

      const { error } = await supabase
        .from('meals')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Invalidate the query to refetch the data
      await queryClient.invalidateQueries({ queryKey: ['seller-meals'] });
      await queryClient.invalidateQueries({ queryKey: ['edit-meal', id] });

      toast({
        title: "Success",
        description: "Meal updated successfully",
      });

      navigate('/seller/meals');
    } catch (error: any) {
      console.error('Error updating meal:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update meal. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <main className="container mx-auto px-4 py-6 max-w-[600px]">
        <EditMealHeader />
        <EditMealForm
          formData={formData}
          onChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </main>
    </div>
  );
}
